import $ from "jquery";
import React, { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import { Provider } from "react-redux";
import "./App.css";
import Header from "./components/Header";
import HelpText from "./components/HelpText";

import { Collective } from "./types/Collective";
import store from "./redux/store";
import ErrorBoundary from "./components/ErrorBoundary";
import { Member } from "./types/Member";

const AccountData = lazy(() => import("./pages/AccountData/AccountData"));
const AccountPassword = lazy(() => import("./pages/AccountPassword/AccountPassword"));
const Contact = lazy(() => import("./pages/Contact"));
const DocumentsList = lazy(() => import("./pages/Documents/DocumentsList"));
const Home = lazy(() => import("./pages/Home"));
const FieldList = lazy(() => import("./pages/Fields/FieldsList"));
const ReportsList = lazy(() => import("./pages/Reports/ReportsList"));
const MapPage = lazy(() => import("./pages/Map/Map"));
const KPIsList = lazy(() => import("./pages/KPI/KPIPage"));
const KPIAuthorizationOverview = lazy(
  () => import("./pages/KPI/Authorization/KPIAuthorizationOverview")
);

declare global {
  interface Window {
    pageName?: string;
    years?: number[];
    collectives?: Collective[];
    members?: Member[];
    currentUsername?: string;
    topBarLoggedOut?: boolean;
    kpisEnabled?: boolean;
    kpiStartYear?: number;
    kpiAuthorizationsAvailable?: boolean;
  }
}

function App() {
  const {
    pageName,
    years,
    collectives,
    members,
    currentUsername,
    topBarLoggedOut,
    location,
    kpisEnabled,
    kpiAuthorizationsAvailable,
  } = window;

  const [helpToggled, toggleHelp] = useState(false);
  const [currentYear, currentCollective] = useMemo(() => {
    const [year, collective] = location.pathname.split("/").slice(1, 5);
    return [parseInt(year), collective];
  }, [location.pathname]);
  useEffect(() => {
    $("#loading").hide();
  }, []);
  return (
    <BrowserRouter basename={`/${currentYear}/${currentCollective}/`}>
      <Header
        loggedOut={topBarLoggedOut}
        allCollectives={collectives || []}
        allMembers={members || []}
        allYears={years || [new Date().getFullYear()]}
        currentUsername={currentUsername || "user"}
        currentYear={currentYear}
        currentCollective={currentCollective}
        onToggleHelp={() => toggleHelp(!helpToggled)}
      />
      {pageName?.indexOf("wachtwoord") === -1 && pageName !== "login" ? (
        <HelpText helpToggled={helpToggled} onToggleHelp={() => toggleHelp(!helpToggled)} />
      ) : null}
      <Provider store={store}>
        <Switch>
          <ErrorBoundary>
            <Suspense
              fallback={
                <div className="loading-container">
                  <div className="c-loading"></div>
                </div>
              }
            >
              <Route path="/home">
                <Home currentCollective={currentCollective} />
              </Route>
              <Route path="/kaart">
                <MapPage currentYear={currentYear} currentCollective={currentCollective} />
              </Route>
              <Route path="/percelen">
                <FieldList currentYear={currentYear} currentCollective={currentCollective} />
              </Route>
              <Route path="/meldingen">
                <ReportsList currentYear={currentYear} currentCollective={currentCollective} />
              </Route>
              <Route path="/documenten">
                <DocumentsList currentYear={currentYear} currentCollective={currentCollective} />
              </Route>
              <Route path="/kpis">
                <KPIsList currentYear={currentYear} currentCollective={currentCollective} />
              </Route>
              {kpiAuthorizationsAvailable ? (
                <Route path="/machtigingen">
                  <KPIAuthorizationOverview year={currentYear} />
                </Route>
              ) : null}
              <Route path="/contact">
                <Contact currentYear={currentYear} currentCollective={currentCollective} />
              </Route>
              <Route path="/account">
                <AccountPassword />
              </Route>
              <Route path="/gegevens">
                <AccountData />
              </Route>
            </Suspense>
          </ErrorBoundary>
        </Switch>
      </Provider>
    </BrowserRouter>
  );
}

export default App;
